import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiBriefcase, FiGift, FiUsers, FiShoppingBag, FiClock, FiStar} = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiShoppingBag,
      title: 'Fresh Daily Baking',
      description: 'All our products are baked fresh daily using traditional recipes and premium ingredients.',
      features: ['Traditional Chinese pastries', 'Fresh bread and buns', 'Seasonal specialties', 'Custom orders available']
    },
    {
      icon: FiGift,
      title: 'Special Orders',
      description: 'We offer custom orders for special occasions, celebrations, and events.',
      features: ['Birthday cakes', 'Wedding pastries', 'Corporate orders', 'Holiday specials']
    },
    {
      icon: FiUsers,
      title: 'Catering Services',
      description: 'Perfect for events, meetings, and gatherings with our delicious baked goods.',
      features: ['Office catering', 'Party platters', 'Event planning support', 'Bulk discounts']
    },
    {
      icon: FiBriefcase,
      title: 'Wholesale Supply',
      description: 'We supply restaurants, cafes, and businesses with our quality baked products.',
      features: ['Restaurant supply', 'Cafe partnerships', 'Regular deliveries', 'Competitive pricing']
    }
  ];

  const additionalServices = [
    {
      icon: FiClock,
      title: 'Early Morning Service',
      description: 'Open early to serve fresh breakfast items and morning pastries.'
    },
    {
      icon: FiStar,
      title: 'Quality Guarantee',
      description: 'We stand behind the quality of every item with our satisfaction guarantee.'
    }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-screen">
      {/* Hero Section - Light yellow header with red circles */}
      <section className="bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] text-black py-20 relative overflow-hidden">
        {/* Red circle templates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 100, opacity: 0.3 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-16 h-16 bg-[#ff8080] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-20 w-20 h-20 bg-[#ffcc80] rounded-full blur-xl"
          />
          <motion.div
            initial={{ rotate: 0, scale: 0.8 }}
            animate={{ rotate: 360, scale: 1 }}
            transition={{ duration: 25, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/2 left-1/4 w-24 h-24 bg-[#ff9999] rounded-full opacity-20 blur-2xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.2 }} 
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Our Services
          </motion.h1>
          <motion.p 
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.4 }} 
            className="text-xl max-w-3xl mx-auto"
          >
            Comprehensive bakery services to meet all your needs, from daily fresh items to special occasion orders
          </motion.p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What We Offer</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From fresh daily baking to custom orders, we provide comprehensive bakery services to serve our community's needs.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index} 
                initial={{ y: 30, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                transition={{ delay: index * 0.1 }} 
                className="bg-white p-8 rounded-lg shadow-lg border border-gray-100"
              >
                <div className="flex items-center mb-6">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-red-600 mr-4" />
                  <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <div className="w-2 h-2 bg-red-600 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 bg-gray-50 relative overflow-hidden">
        {/* Moving elements - 30% lighter */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: -100, opacity: 0.3 }}
            transition={{ duration: 18, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/4 right-10 w-14 h-14 bg-[#ff9999] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: -80, opacity: 0.3 }}
            transition={{ duration: 12, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-10 left-1/3 w-18 h-18 bg-[#ff8080] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Additional Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div 
                key={index} 
                initial={{ y: 30, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                transition={{ delay: index * 0.2 }} 
                className="bg-white p-6 rounded-lg shadow-lg text-center"
              >
                <SafeIcon icon={service.icon} className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hours & Contact - Light yellow header with red circles */}
      <section className="py-16 bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] text-black relative overflow-hidden">
        {/* Red circle templates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ scale: 0.5, rotate: 0 }}
            animate={{ scale: 1, rotate: 180 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-32 h-32 bg-[#ff6666] rounded-full opacity-20 blur-3xl"
          />
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: -50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-1/4 w-16 h-16 bg-[#ffcc80] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Operating Hours</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span>Monday - Sunday:</span>
                  <span>7:00 AM - 8:00 PM</span>
                </div>
                <div className="border-t border-yellow-300 pt-4">
                  <p className="text-black">We're open every day to serve you fresh baked goods and traditional pastries.</p>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Need More Information?</h2>
              <p className="mb-6 text-black">Have questions about our services or need a custom order? Contact us today and we'll be happy to help.</p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <SafeIcon icon={FiShoppingBag} className="w-5 h-5 mr-3 text-red-600" />
                  <span>Call: +14164659103</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiBriefcase} className="w-5 h-5 mr-3 text-red-600" />
                  <span>Visit: 353 Broadview Ave, Toronto</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Services;